import { useState } from "react";
import { styles } from "../styles";
import {
  createPurchaseOrder,
  VARIANCE_REASON_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
} from "../models/models";
import { hasCapability, isMine, displayName } from "../utils/capabilities";
import { createAuditEntry } from "../utils/audit";
import AuditTrail from "../components/AuditTrail";
import BackButton from "../components/BackButton";
import PaymentEntryModal from "../components/PaymentEntryModal";
import AgingSummary from "../components/AgingSummary";

const PAYMENT_METHOD_LABEL = Object.fromEntries(
  PAYMENT_METHOD_OPTIONS.map((o) => [o.value, o.label])
);

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const money = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });
const poTotal = (po) =>
  (po.items || []).reduce((s, i) => s + num(i.qty) * num(i.unitCost), 0);

/**
 * PO board — everything on one page: create a PO (or request) inline with all
 * fields, then track/search the list below. No separate detail page. After
 * creation a PO shows a Received button (and Approve for requests).
 */
export default function POsPage({
  purchaseOrdersRaw: rawPOs = [],
  jobsRaw = [],
  customers = [],
  vendorsRaw = [],
  actions,
  role,
  capabilities,
  user,
  profile = {},
  onShowVendors,
  onBack,
}) {
  const myName = displayName(profile, user?.email);
  const canCreatePOs = hasCapability(role, capabilities, "createPOs");
  const canManage = role === "admin" || role === "manager";
  const canVerifyBills = hasCapability(
    role,
    capabilities,
    "verifyVendorBills"
  );
  const canApprovePayment = hasCapability(role, capabilities, "approvePayment");
  const canMarkPaid = hasCapability(role, capabilities, "markPaid");
  const canViewApAging = hasCapability(role, capabilities, "viewAPAging");

  // AP payment-entry modal target (Phase B). Holds the PO whose vendor
  // bill we're recording a payment against.
  const [vendorPaymentTarget, setVendorPaymentTarget] = useState(null);
  // Techs (and anyone without viewAllPOs) only see POs they placed themselves.
  const purchaseOrdersRaw = hasCapability(role, capabilities, "viewAllPOs")
    ? rawPOs
    : rawPOs.filter((p) => isMine(p, user));

  // Build a synthetic "bill" list for AgingSummary from verified vendor
  // bills on PO docs. Map vendorBill fields onto the shape AgingSummary
  // expects (grandTotal, paidAmount, dueDate, finalizedAt). Verified-not-
  // yet-paid bills feed the AP aging buckets. NOTE: must declare after
  // the scoped purchaseOrdersRaw above — referencing it before its
  // local const initialization hit the TDZ (Justin caught 2026-06-15).
  const apBills = (purchaseOrdersRaw || [])
    .filter((p) => p.vendorBill?.verifiedAt)
    .map((p) => ({
      id: p.id,
      grandTotal: parseFloat(p.vendorBill?.totalAmount) || 0,
      paidAmount: parseFloat(p.vendorBill?.paidAmount) || 0,
      dueDate: p.vendorBill?.dueDate || "",
      finalizedAt: p.vendorBill?.verifiedAt || "",
      paymentStatus: p.vendorBill?.paymentStatus || "unpaid",
    }));

  // create-card state
  const [showCreate, setShowCreate] = useState(false);
  const [custId, setCustId] = useState("");
  const [locId, setLocId] = useState("");
  const [jobId, setJobId] = useState("");
  // Vendor lookup uses the vendor master (Phase A0). vendorId references
  // the vendor doc; vendorName denormalized to PO at write time so
  // renames don't rewrite history.
  const [vendorId, setVendorId] = useState("");
  // Sorted alphabetically per [[feedback-alphabetical-lists]]; active only
  // so retired vendors don't clutter the dropdown.
  const activeVendors = [...vendorsRaw]
    .filter((v) => v.status === "active")
    .sort((a, b) =>
      (a.displayName || a.name || "")
        .toLowerCase()
        .localeCompare((b.displayName || b.name || "").toLowerCase())
    );
  const vendorById = (id) => vendorsRaw.find((v) => v.id === id) || null;
  const [expectedArrival, setExpectedArrival] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  // list state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [showAuditId, setShowAuditId] = useState(null);

  // inline edit state (edit a PO right in its expanded row)
  const [editingId, setEditingId] = useState(null);
  const [eVendor, setEVendor] = useState("");
  const [eExpected, setEExpected] = useState("");
  const [eItems, setEItems] = useState([]);
  const [eNotes, setENotes] = useState("");

  // Per-PO vendor-bill entry state (Phase A2). Keyed by PO id so multiple
  // expanded rows can hold draft input without colliding. Only the
  // currently-billing PO has an entry in this object.
  const [billDraft, setBillDraft] = useState({});
  const updateBillDraft = (poId, patch) =>
    setBillDraft((prev) => ({
      ...prev,
      [poId]: { ...(prev[poId] || {}), ...patch },
    }));

  // Append an audit entry to a PO's trail.
  const withAudit = (po, action, details = "") => [
    ...(po.auditTrail || []),
    createAuditEntry(user?.email, action, details),
  ];

  const custName = (id) => customers.find((c) => c.id === id)?.name || "—";
  const jobById = (id) => jobsRaw.find((j) => j.id === id);
  const jobLabel = (id) => {
    const j = jobById(id);
    if (!j) return "—";
    return `${j.jobNumber ? `${j.jobNumber} — ` : ""}${j.title || ""}`;
  };

  // Lead-time badge for a PO that's been ordered but not yet received.
  // Renders a color-coded "Expected: YYYY-MM-DD (X days)" pill so the admin
  // can scan the list without expanding every row. Justin's pain: lead
  // times getting lost in email — surfacing on the collapsed row kills the
  // "let me search Gmail for the order confirmation" cycle.
  // Returns null when the badge isn't relevant (not ordered, no date set,
  // already received).
  const arrivalBadge = (po) => {
    if (po.status !== "ordered") return null;
    if (!po.expectedArrival) {
      return {
        label: "No arrival date",
        bg: "#f3f4f6",
        color: "#6b7280",
        border: "#e5e7eb",
      };
    }
    // ISO YYYY-MM-DD; parse as local-midnight to avoid TZ-shifting the
    // boundary (new Date("2026-06-20") parses as UTC and renders -1 day
    // in negative-offset zones).
    const [y, m, d] = po.expectedArrival
      .split("-")
      .map((s) => parseInt(s, 10));
    if (!y || !m || !d) return null;
    const arrival = new Date(y, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round(
      (arrival.getTime() - today.getTime()) / 86400000
    );
    let label;
    let palette;
    if (diffDays < 0) {
      label = `Overdue ${Math.abs(diffDays)} day${
        Math.abs(diffDays) === 1 ? "" : "s"
      } · ${po.expectedArrival}`;
      palette = { bg: "#fee2e2", color: "#b91c1c", border: "#fca5a5" };
    } else if (diffDays === 0) {
      label = `Arriving today · ${po.expectedArrival}`;
      palette = { bg: "#fff3cd", color: "#92400e", border: "#facc15" };
    } else if (diffDays <= 7) {
      label = `In ${diffDays} day${diffDays === 1 ? "" : "s"} · ${
        po.expectedArrival
      }`;
      palette = { bg: "#fff3cd", color: "#92400e", border: "#facc15" };
    } else {
      label = `In ${diffDays} days · ${po.expectedArrival}`;
      palette = { bg: "#dde7f4", color: "#1a5fb4", border: "#bcd0ee" };
    }
    return { label, ...palette };
  };

  const locationsForCustomer =
    customers.find((c) => c.id === custId)?.locations || [];
  // PO picker hides any job that isn't in the active queue.
  //   * closed / canceled — admin-set end-of-life
  //   * scheduled / skipped — future or skipped maintenance visits under a
  //     contract; a single contract spawns N visit jobs and only the one
  //     admin has activated is "open." Showing all N would dump every
  //     visit-job onto the dropdown (Justin's complaint 2026-06-15).
  //   * billFinalized true — legacy single-finalize path is done billing
  //   * bills[] type "final" — multi-bill path is done billing
  const jobsForCustomer = jobsRaw.filter((j) => {
    if (j.customerId !== custId || j.locationId !== locId) return false;
    if (["closed", "canceled", "scheduled", "skipped"].includes(j.status))
      return false;
    if (j.billFinalized) return false;
    if ((j.bills || []).some((b) => b.type === "final")) return false;
    return true;
  });

  const addItem = () =>
    setItems((p) => [
      ...p,
      { id: newId(), name: "", partNumber: "", qty: "", unitCost: "" },
    ]);
  const editItem = (id, f, v) =>
    setItems((p) => p.map((i) => (i.id === id ? { ...i, [f]: v } : i)));
  const removeItem = (id) => setItems((p) => p.filter((i) => i.id !== id));

  const resetForm = () => {
    setCustId("");
    setLocId("");
    setJobId("");
    setVendorId("");
    setExpectedArrival("");
    setNotes("");
    setItems([]);
  };

  const handleCreate = async () => {
    if (
      !custId ||
      !jobId ||
      !vendorId ||
      !items.some((i) => (i.name || "").trim())
    ) {
      return;
    }
    const job = jobById(jobId);
    const v = vendorById(vendorId);
    const vendorName = v?.displayName || v?.name || "";
    const now = new Date().toISOString();
    const base = {
      ...createPurchaseOrder(),
      jobId,
      customerId: job?.customerId || "",
      locationId: job?.locationId || "",
      vendorId,
      vendorName,
      expectedArrival,
      notes,
      items,
      createdBy: user?.email || "unknown",
      createdByUid: user?.uid || "",
    };
    setBusy(true);
    if (canCreatePOs) {
      // Created as an open/ordered PO with a number assigned.
      await actions.addPurchaseOrderNumbered({
        ...base,
        status: "ordered",
        orderedAt: now,
        auditTrail: [createAuditEntry(user?.email, "Created PO")],
      });
    } else {
      // Submitted as a request for an authorized person to issue.
      await actions.addPurchaseOrder({
        ...base,
        status: "requested",
        requestedBy: user?.email || "",
        requestedByName: myName,
        auditTrail: [createAuditEntry(user?.email, "Requested PO")],
      });
    }
    setBusy(false);
    resetForm();
    setShowCreate(false);
  };

  const approve = (po) =>
    actions.markPOOrdered(
      po.id,
      po.expectedArrival,
      withAudit(po, "Approved & Ordered")
    );
  const receive = (po) => {
    if (
      !window.confirm(
        "Mark this PO received? Its items will feed the job's Estimated Bill."
      )
    )
      return;
    actions.updatePurchaseOrder(po.id, {
      status: "received",
      receivedAt: new Date().toISOString(),
      auditTrail: withAudit(po, "Marked Received"),
      updatedAt: new Date().toISOString(),
    });
  };

  // Compute PO subtotal from items (= what we agreed to pay). Phase A2
  // compares this against the vendor's invoice total to flag variance.
  const poSubtotal = (po) => poTotal(po);

  // Submit the vendor-bill verification for a PO. Variance reason is
  // required when the vendor's invoice doesn't match the PO subtotal —
  // bookkeeper needs to document WHY there's a difference, not just
  // accept it silently. "Other" reason additionally requires a note.
  const submitVerifyBill = async (po) => {
    const draft = billDraft[po.id] || {};
    const invoiceNumber = (draft.invoiceNumber || "").trim();
    const totalAmount = num(draft.totalAmount);
    const receivedDate = draft.receivedDate || "";
    const variance = totalAmount - poSubtotal(po);
    const sub = poSubtotal(po);
    const variancePct = sub > 0 ? (variance / sub) * 100 : 0;
    const varianceReason = draft.varianceReason || "";
    const varianceNote = (draft.varianceNote || "").trim();

    if (!invoiceNumber) {
      window.alert("Vendor invoice number is required.");
      return;
    }
    if (totalAmount <= 0) {
      window.alert("Vendor bill total must be greater than zero.");
      return;
    }
    if (Math.abs(variance) > 0.005 && !varianceReason) {
      window.alert(
        "Variance reason is required when the vendor's bill total " +
          "doesn't match the PO. Pick the closest reason from the dropdown."
      );
      return;
    }
    if (varianceReason === "other" && !varianceNote) {
      window.alert(
        "When 'Other' is selected for variance reason, a note describing the difference is required."
      );
      return;
    }

    await actions.verifyVendorBill(po.id, {
      invoiceNumber,
      receivedDate,
      totalAmount,
      varianceAmount: Number(variance.toFixed(2)),
      variancePct: Number(variancePct.toFixed(2)),
      varianceReason,
      varianceNote,
    });
    // Clear this PO's draft so the entry form resets cleanly.
    setBillDraft((prev) => {
      const next = { ...prev };
      delete next[po.id];
      return next;
    });
  };

  // --- inline edit ---
  const startEdit = (po) => {
    setEditingId(po.id);
    // Carry the existing vendorId forward so the dropdown re-selects;
    // fall back to empty if the PO predates vendor master.
    setEVendor(po.vendorId || "");
    setEExpected(po.expectedArrival || "");
    setEItems(po.items || []);
    setENotes(po.notes || "");
  };
  const eAddItem = () =>
    setEItems((p) => [
      ...p,
      { id: newId(), name: "", partNumber: "", qty: "", unitCost: "" },
    ]);
  const eEditItem = (id, f, v) =>
    setEItems((p) => p.map((i) => (i.id === id ? { ...i, [f]: v } : i)));
  const eRemoveItem = (id) => setEItems((p) => p.filter((i) => i.id !== id));
  const saveEdit = async (po) => {
    const v = vendorById(eVendor);
    await actions.updatePurchaseOrder(po.id, {
      vendorId: eVendor,
      vendorName: v?.displayName || v?.name || po.vendorName || "",
      expectedArrival: eExpected,
      items: eItems,
      notes: eNotes,
      auditTrail: withAudit(po, "Edited PO"),
      updatedAt: new Date().toISOString(),
    });
    setEditingId(null);
  };

  const canEditPO = (po) =>
    canCreatePOs ||
    canManage ||
    (po.createdByUid === user?.uid && po.status === "requested");
  const remove = (po) => {
    if (!window.confirm("Delete this purchase order?")) return;
    actions.deletePurchaseOrder(po.id);
  };

  const matchesSearch = (po) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    const hay = [
      po.poNumber,
      po.vendor,         // legacy free-text (pre-A0)
      po.vendorName,     // Phase A0 denormalized vendor
      jobLabel(po.jobId),
      custName(po.customerId),
      ...(po.items || []).flatMap((i) => [i.name, i.partNumber]),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(term);
  };

  const filtered = [...purchaseOrdersRaw]
    .filter((po) => statusFilter === "all" || po.status === statusFilter)
    .filter(matchesSearch)
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  const smallInput = { ...styles.input, width: "90px", margin: "2px" };

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>Purchase Orders</h2>

      {canViewApAging && (
        <AgingSummary title="AP Aging — vendor bills due" bills={apBills} />
      )}

      <button style={styles.button} onClick={() => setShowCreate((s) => !s)}>
        {showCreate ? "Hide" : canCreatePOs ? "New PO" : "Request PO"}
      </button>

      {showCreate && (
        <div style={styles.card}>
          <select
            value={custId}
            onChange={(e) => {
              setCustId(e.target.value);
              setLocId("");
              setJobId("");
            }}
          >
            <option value="">Select customer</option>
            {[...customers]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
          {custId && (
            <div style={{ marginTop: "6px" }}>
              <select
                value={locId}
                onChange={(e) => {
                  setLocId(e.target.value);
                  setJobId("");
                }}
              >
                <option value="">Select facility</option>
                {[...locationsForCustomer]
                  .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                  .map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
              </select>
            </div>
          )}
          {custId && locId && (
            <div style={{ marginTop: "6px" }}>
              <select value={jobId} onChange={(e) => setJobId(e.target.value)}>
                <option value="">Select job</option>
                {jobsForCustomer.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.jobNumber ? `${j.jobNumber} — ` : ""}
                    {j.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          <br />
          <label style={{ fontSize: "13px" }}>Vendor: </label>
          <select
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
          >
            <option value="">Select vendor</option>
            {activeVendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.displayName || v.name}
              </option>
            ))}
          </select>
          {onShowVendors && (
            <button
              type="button"
              style={{
                ...styles.button,
                marginLeft: "8px",
                fontSize: "12px",
              }}
              onClick={onShowVendors}
              title="Open Vendors page to add a new vendor"
            >
              + New Vendor
            </button>
          )}
          {activeVendors.length === 0 && (
            <span
              style={{
                fontSize: "11px",
                color: "#a15c00",
                marginLeft: "8px",
              }}
            >
              No active vendors yet — add one from the Vendors page first.
            </span>
          )}

          {/* Items */}
          <div style={{ marginTop: "10px" }}>
            {items.map((i) => (
              <div key={i.id} style={{ fontSize: "12px", marginTop: "2px" }}>
                <input
                  style={{ ...smallInput, width: "150px" }}
                  placeholder="Part / material"
                  value={i.name}
                  onChange={(e) => editItem(i.id, "name", e.target.value)}
                />
                <input
                  style={smallInput}
                  placeholder="Part #"
                  value={i.partNumber}
                  onChange={(e) => editItem(i.id, "partNumber", e.target.value)}
                />
                <input
                  style={smallInput}
                  type="number"
                  placeholder="Qty"
                  value={i.qty}
                  onChange={(e) => editItem(i.id, "qty", e.target.value)}
                />
                <input
                  style={smallInput}
                  type="number"
                  placeholder="Unit cost"
                  value={i.unitCost}
                  onChange={(e) => editItem(i.id, "unitCost", e.target.value)}
                />
                <span>= {money(num(i.qty) * num(i.unitCost))}</span>{" "}
                <button
                  style={{ ...styles.button, color: "red", padding: "2px 6px" }}
                  onClick={() => removeItem(i.id)}
                >
                  x
                </button>
              </div>
            ))}
            <button style={styles.button} onClick={addItem}>
              + Item
            </button>
          </div>

          <textarea
            style={styles.textarea}
            placeholder="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          <div style={{ marginTop: "6px" }}>
            <label style={{ fontSize: "13px" }}>
              Expected arrival (if shipped / back-ordered):{" "}
            </label>
            <input
              type="date"
              value={expectedArrival}
              onChange={(e) => setExpectedArrival(e.target.value)}
            />
          </div>

          <div style={{ fontSize: "13px", fontWeight: "bold", marginTop: "8px" }}>
            PO total cost:{" "}
            {money(items.reduce((s, i) => s + num(i.qty) * num(i.unitCost), 0))}
          </div>

          {(() => {
            const canSubmit =
              custId &&
              jobId &&
              vendorId &&
              items.some((i) => (i.name || "").trim());
            return (
              <button
                style={styles.button}
                onClick={handleCreate}
                disabled={!canSubmit || busy}
                title={
                  canSubmit
                    ? ""
                    : "Pick a customer + job, enter a vendor, and add at least one item."
                }
              >
                {busy
                  ? "Saving..."
                  : canCreatePOs
                  ? "Create PO"
                  : "Submit Request"}
              </button>
            );
          })()}
          {!canCreatePOs && (
            <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
              You don't have PO permission, so this is submitted as a request
              for an authorized person to issue.
            </div>
          )}
        </div>
      )}

      {/* Search + filter */}
      <div style={{ margin: "10px 0" }}>
        <input
          style={styles.input}
          placeholder="Search by PO #, vendor, job, customer, or part"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div style={{ fontSize: "13px", marginTop: "4px" }}>
          <label>Status: </label>
          {/* AP-side options (Bill Verified status, "awaiting vendor bill"
              wording, AP queue pill) only render for billing-side users.
              Techs see plain Received without bookkeeper context. */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="requested">Requested</option>
            <option value="draft">Draft</option>
            <option value="ordered">Ordered</option>
            <option value="received">
              {canViewApAging || canVerifyBills
                ? "Received (awaiting vendor bill)"
                : "Received"}
            </option>
            {(canViewApAging || canVerifyBills) && (
              <option value="bill_verified">Bill Verified</option>
            )}
          </select>
          {(canViewApAging || canVerifyBills) &&
            (() => {
              const awaiting = purchaseOrdersRaw.filter(
                (p) => p.status === "received" && !p.vendorBill?.verifiedAt
              ).length;
              if (awaiting === 0) return null;
              return (
                <span
                  onClick={() => setStatusFilter("received")}
                  style={{
                    marginLeft: "10px",
                    padding: "2px 8px",
                    background: "#fff3cd",
                    color: "#92400e",
                    border: "1px solid #facc15",
                    borderRadius: "4px",
                    fontSize: "12px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                  title="Click to filter — POs received but vendor bill not yet entered"
                >
                  AP queue: {awaiting} awaiting vendor bill
                </span>
              );
            })()}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p style={{ fontSize: "13px", color: "#666" }}>No purchase orders.</p>
      ) : (
        filtered.map((po) => {
          const open = expandedId === po.id;
          return (
            <div key={po.id} style={styles.card}>
              <div
                style={{ cursor: "pointer" }}
                onClick={() => setExpandedId(open ? null : po.id)}
              >
                {open ? "▾" : "▸"}{" "}
                <strong>{po.poNumber || "PO (request)"}</strong>
                <span
                  style={{
                    fontSize: "11px",
                    marginLeft: "8px",
                    padding: "1px 6px",
                    background:
                      po.status === "received"
                        ? "#d6f5dd"
                        : po.status === "requested"
                        ? "#fff3cd"
                        : "#e0e0e0",
                    borderRadius: "4px",
                  }}
                >
                  {po.status}
                </span>
                {(() => {
                  const badge = arrivalBadge(po);
                  if (!badge) return null;
                  return (
                    <span
                      style={{
                        fontSize: "11px",
                        marginLeft: "6px",
                        padding: "1px 6px",
                        background: badge.bg,
                        color: badge.color,
                        border: `1px solid ${badge.border}`,
                        borderRadius: "4px",
                        fontWeight: 600,
                      }}
                    >
                      {badge.label}
                    </span>
                  );
                })()}
                <br />
                <span style={{ fontSize: "12px", color: "#666", marginLeft: "14px" }}>
                  {jobLabel(po.jobId)} • {custName(po.customerId)}
                  {/* vendorName is denormalized at write time (Phase A0);
                      fall back to legacy free-text po.vendor for pre-A0
                      data so it still displays during the cutover. */}
                  {(po.vendorName || po.vendor)
                    ? ` • ${po.vendorName || po.vendor}`
                    : ""}
                  {(po.createdByName || po.createdBy)
                    ? ` • placed by ${po.createdByName || po.createdBy}`
                    : ""}
                  {" • "}
                  {money(poTotal(po))}
                </span>
              </div>

              {open && (
                <div style={{ marginLeft: "14px", marginTop: "6px", fontSize: "13px" }}>
                  {editingId === po.id ? (
                    /* ---- inline editor ---- */
                    <div>
                      <label style={{ fontSize: "12px" }}>Vendor: </label>
                      <select
                        value={eVendor}
                        onChange={(e) => setEVendor(e.target.value)}
                      >
                        <option value="">Select vendor</option>
                        {activeVendors.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.displayName || v.name}
                          </option>
                        ))}
                      </select>
                      <br />
                      <label style={{ fontSize: "12px" }}>Expected arrival: </label>
                      <input
                        type="date"
                        value={eExpected}
                        onChange={(e) => setEExpected(e.target.value)}
                      />
                      <div style={{ marginTop: "6px" }}>
                        <strong style={{ fontSize: "12px" }}>Items</strong>
                        {eItems.map((i) => (
                          <div key={i.id} style={{ fontSize: "12px", marginTop: "2px" }}>
                            <input
                              style={{ ...smallInput, width: "150px" }}
                              placeholder="Part / material"
                              value={i.name}
                              onChange={(e) => eEditItem(i.id, "name", e.target.value)}
                            />
                            <input
                              style={smallInput}
                              placeholder="Part #"
                              value={i.partNumber}
                              onChange={(e) => eEditItem(i.id, "partNumber", e.target.value)}
                            />
                            <input
                              style={smallInput}
                              type="number"
                              placeholder="Qty"
                              value={i.qty}
                              onChange={(e) => eEditItem(i.id, "qty", e.target.value)}
                            />
                            <input
                              style={smallInput}
                              type="number"
                              placeholder="Unit cost"
                              value={i.unitCost}
                              onChange={(e) => eEditItem(i.id, "unitCost", e.target.value)}
                            />
                            <button
                              style={{ ...styles.button, color: "red", padding: "2px 6px" }}
                              onClick={() => eRemoveItem(i.id)}
                            >
                              x
                            </button>
                          </div>
                        ))}
                        <button style={styles.button} onClick={eAddItem}>
                          + Item
                        </button>
                      </div>
                      <textarea
                        style={styles.textarea}
                        placeholder="Notes"
                        value={eNotes}
                        onChange={(e) => setENotes(e.target.value)}
                      />
                      <div>
                        <button style={styles.button} onClick={() => saveEdit(po)}>
                          Save Changes
                        </button>
                        <button style={styles.button} onClick={() => setEditingId(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* ---- read-only details ---- */
                    <>
                      {(po.items || []).length > 0 && (
                        <ul style={{ margin: "2px 0", paddingLeft: "18px" }}>
                          {po.items.map((i) => (
                            <li key={i.id}>
                              {i.name || "Item"}
                              {i.partNumber ? ` (#${i.partNumber})` : ""} — qty{" "}
                              {num(i.qty)} @ {money(num(i.unitCost))} ={" "}
                              {money(num(i.qty) * num(i.unitCost))}
                            </li>
                          ))}
                        </ul>
                      )}
                      {po.expectedArrival && (
                        <div style={{ color: "#666" }}>
                          Expected arrival: {po.expectedArrival}
                        </div>
                      )}
                      {po.notes && <div>Notes: {po.notes}</div>}
                      {po.status === "requested" && (po.requestedByName || po.requestedBy) && (
                        <div style={{ color: "#666" }}>
                          Requested by {po.requestedByName || po.requestedBy}
                        </div>
                      )}

                      {/* ---- Phase A2 + B: Vendor bill verification block ----
                          Gated to billing-side users only — techs shouldn't
                          see vendor bill numbers, variance, payment status,
                          or the AP queue context. */}
                      {(canViewApAging || canVerifyBills) &&
                      po.vendorBill?.verifiedAt ? (
                        // Read-only display of the verified bill.
                        <div
                          style={{
                            marginTop: "10px",
                            padding: "8px 10px",
                            background: "#dde7f4",
                            border: "1px solid #bcd0ee",
                            borderRadius: "6px",
                            fontSize: "12px",
                          }}
                        >
                          <strong style={{ color: "#1a5fb4" }}>
                            Vendor bill verified
                          </strong>
                          <div>
                            Invoice #{po.vendorBill.invoiceNumber || "—"} —
                            total {money(num(po.vendorBill.totalAmount))}
                            {po.vendorBill.receivedDate
                              ? ` (received ${po.vendorBill.receivedDate})`
                              : ""}
                          </div>
                          {Math.abs(num(po.vendorBill.varianceAmount)) > 0.005 && (
                            <div>
                              Variance vs PO:{" "}
                              <strong
                                style={{
                                  color:
                                    num(po.vendorBill.varianceAmount) > 0
                                      ? "#b91c1c"
                                      : "#1a7f37",
                                }}
                              >
                                {num(po.vendorBill.varianceAmount) > 0 ? "+" : ""}
                                {money(num(po.vendorBill.varianceAmount))} (
                                {num(po.vendorBill.variancePct).toFixed(1)}%)
                              </strong>{" "}
                              — {VARIANCE_REASON_OPTIONS.find(
                                (o) => o.value === po.vendorBill.varianceReason
                              )?.label || po.vendorBill.varianceReason || "no reason"}
                              {po.vendorBill.varianceNote && (
                                <div style={{ fontStyle: "italic" }}>
                                  Note: {po.vendorBill.varianceNote}
                                </div>
                              )}
                            </div>
                          )}
                          <div style={{ color: "#666" }}>
                            Verified by{" "}
                            {po.vendorBill.verifiedByName ||
                              po.vendorBill.verifiedBy ||
                              "—"}{" "}
                            on{" "}
                            {new Date(po.vendorBill.verifiedAt).toLocaleString()}
                          </div>
                          {/* Phase B AP: approve + record payment + history. */}
                          {(() => {
                            const vb = po.vendorBill || {};
                            const total = num(vb.totalAmount);
                            const paid = num(vb.paidAmount);
                            const status =
                              vb.paymentStatus ||
                              (paid + 0.005 >= total
                                ? "paid"
                                : paid > 0
                                ? "partial"
                                : "unpaid");
                            const palette = {
                              paid: {
                                bg: "#d6f5dd",
                                color: "#1a7f37",
                                border: "#a7e3b3",
                              },
                              partial: {
                                bg: "#fff3cd",
                                color: "#92400e",
                                border: "#facc15",
                              },
                              unpaid: {
                                bg: "#f3f4f6",
                                color: "#6b7280",
                                border: "#e5e7eb",
                              },
                            }[status];
                            return (
                              <div
                                style={{
                                  marginTop: "8px",
                                  paddingTop: "6px",
                                  borderTop: "1px dashed #bcd0ee",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "11px",
                                    padding: "2px 8px",
                                    background: palette.bg,
                                    color: palette.color,
                                    border: `1px solid ${palette.border}`,
                                    borderRadius: "4px",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {status === "paid"
                                    ? "Paid"
                                    : status === "partial"
                                    ? `Partial ${money(paid)}`
                                    : "Unpaid"}
                                </span>
                                {vb.approvedForPayment ? (
                                  <span
                                    style={{
                                      marginLeft: "6px",
                                      fontSize: "11px",
                                      padding: "2px 8px",
                                      background: "#e0eaff",
                                      color: "#1a5fb4",
                                      border: "1px solid #bcd0ee",
                                      borderRadius: "4px",
                                      fontWeight: 600,
                                    }}
                                    title={`Approved by ${
                                      vb.approvedByName || vb.approvedBy || "—"
                                    }${
                                      vb.approvedAt
                                        ? ` on ${new Date(
                                            vb.approvedAt
                                          ).toLocaleDateString()}`
                                        : ""
                                    }`}
                                  >
                                    Approved for payment
                                  </span>
                                ) : (
                                  canApprovePayment && (
                                    <button
                                      style={{
                                        ...styles.button,
                                        marginLeft: "6px",
                                        padding: "4px 8px",
                                        background: "#1a5fb4",
                                        color: "white",
                                      }}
                                      onClick={() =>
                                        actions.approveVendorBillForPayment(po.id)
                                      }
                                    >
                                      Approve for payment
                                    </button>
                                  )
                                )}
                                {canMarkPaid && status !== "paid" && (
                                  <button
                                    style={{
                                      ...styles.button,
                                      marginLeft: "6px",
                                      padding: "4px 8px",
                                      background: "#1a7f37",
                                      color: "white",
                                    }}
                                    onClick={() => setVendorPaymentTarget(po)}
                                  >
                                    Record payment
                                  </button>
                                )}
                                {(vb.payments || []).length > 0 && (
                                  <div
                                    style={{
                                      marginTop: "6px",
                                      fontSize: "11px",
                                      color: "#1a5f1f",
                                    }}
                                  >
                                    <strong>Payments:</strong>
                                    {(vb.payments || []).map((p) => (
                                      <div
                                        key={p.id}
                                        style={{ marginLeft: "8px" }}
                                      >
                                        {p.date} ·{" "}
                                        {PAYMENT_METHOD_LABEL[p.method] ||
                                          p.method}{" "}
                                        · {money(p.amount)}
                                        {p.note ? ` — ${p.note}` : ""}
                                        {p.paidByName
                                          ? ` (recorded by ${p.paidByName})`
                                          : ""}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      ) : po.status === "received" && canVerifyBills ? (
                        // Bill entry form for received POs awaiting bill.
                        (() => {
                          const draft = billDraft[po.id] || {};
                          const total = num(draft.totalAmount);
                          const sub = poSubtotal(po);
                          const variance = total > 0 ? total - sub : 0;
                          const variancePct = sub > 0 && total > 0 ? (variance / sub) * 100 : 0;
                          const needsReason = total > 0 && Math.abs(variance) > 0.005;
                          return (
                            <div
                              style={{
                                marginTop: "10px",
                                padding: "10px 12px",
                                background: "#fff8e0",
                                border: "1px solid #f0d27a",
                                borderRadius: "6px",
                                fontSize: "12px",
                              }}
                            >
                              <strong style={{ color: "#8a5a00" }}>
                                Enter vendor bill
                              </strong>
                              <div style={{ color: "#5a4000", marginBottom: "6px" }}>
                                PO subtotal: <strong>{money(sub)}</strong>
                              </div>
                              <div>
                                <label>Vendor invoice #: </label>
                                <input
                                  style={{ ...styles.input, width: "160px" }}
                                  placeholder="e.g. INV-12345"
                                  value={draft.invoiceNumber || ""}
                                  onChange={(e) =>
                                    updateBillDraft(po.id, {
                                      invoiceNumber: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div>
                                <label>Bill total $: </label>
                                <input
                                  style={{ ...styles.input, width: "120px" }}
                                  type="number"
                                  step="0.01"
                                  placeholder={sub.toFixed(2)}
                                  value={draft.totalAmount || ""}
                                  onChange={(e) =>
                                    updateBillDraft(po.id, {
                                      totalAmount: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div>
                                <label>Date received at office: </label>
                                <input
                                  type="date"
                                  value={draft.receivedDate || ""}
                                  onChange={(e) =>
                                    updateBillDraft(po.id, {
                                      receivedDate: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              {total > 0 && (
                                <div
                                  style={{
                                    marginTop: "6px",
                                    padding: "4px 8px",
                                    background:
                                      Math.abs(variance) <= 0.005
                                        ? "#d6f5dd"
                                        : "#fee2e2",
                                    border: `1px solid ${
                                      Math.abs(variance) <= 0.005
                                        ? "#a7e3b3"
                                        : "#fca5a5"
                                    }`,
                                    borderRadius: "4px",
                                    color:
                                      Math.abs(variance) <= 0.005
                                        ? "#1a7f37"
                                        : "#b91c1c",
                                    fontWeight: 600,
                                  }}
                                >
                                  {Math.abs(variance) <= 0.005
                                    ? `Matches PO ✓`
                                    : `Variance ${
                                        variance > 0 ? "+" : ""
                                      }${money(variance)} (${variancePct.toFixed(1)}%) vs PO`}
                                </div>
                              )}
                              {needsReason && (
                                <>
                                  <div style={{ marginTop: "6px" }}>
                                    <label>Variance reason: </label>
                                    <select
                                      value={draft.varianceReason || ""}
                                      onChange={(e) =>
                                        updateBillDraft(po.id, {
                                          varianceReason: e.target.value,
                                        })
                                      }
                                    >
                                      <option value="">— Select —</option>
                                      {VARIANCE_REASON_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                          {o.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  {draft.varianceReason === "other" && (
                                    <div>
                                      <label>Note (required): </label>
                                      <input
                                        style={{ ...styles.input, width: "260px" }}
                                        placeholder="What's the difference?"
                                        value={draft.varianceNote || ""}
                                        onChange={(e) =>
                                          updateBillDraft(po.id, {
                                            varianceNote: e.target.value,
                                          })
                                        }
                                      />
                                    </div>
                                  )}
                                  {draft.varianceReason &&
                                    draft.varianceReason !== "other" && (
                                      <div>
                                        <label>Note (optional): </label>
                                        <input
                                          style={{ ...styles.input, width: "260px" }}
                                          placeholder="Anything else to track"
                                          value={draft.varianceNote || ""}
                                          onChange={(e) =>
                                            updateBillDraft(po.id, {
                                              varianceNote: e.target.value,
                                            })
                                          }
                                        />
                                      </div>
                                    )}
                                </>
                              )}
                              <button
                                style={{
                                  ...styles.button,
                                  background: "#1a5fb4",
                                  color: "white",
                                  marginTop: "6px",
                                }}
                                onClick={() => submitVerifyBill(po)}
                              >
                                Verify bill
                              </button>
                            </div>
                          );
                        })()
                      ) : null}

                      <div style={{ marginTop: "8px" }}>
                        {canEditPO(po) && (
                          <button style={styles.button} onClick={() => startEdit(po)}>
                            Edit
                          </button>
                        )}
                        {po.status === "requested" && (canCreatePOs || canManage) && (
                          <button style={styles.button} onClick={() => approve(po)}>
                            Approve &amp; Order
                          </button>
                        )}
                        {/* Any org member can mark an ordered PO received
                            (e.g. a tech receiving parts on site). */}
                        {po.status === "ordered" && (
                          <button
                            style={{ ...styles.button, background: "#1a7f37", color: "white" }}
                            onClick={() => receive(po)}
                          >
                            Received
                          </button>
                        )}
                        {po.status === "draft" && (canCreatePOs || canManage) && (
                          <button
                            style={{ ...styles.button, background: "#1a7f37", color: "white" }}
                            onClick={() => receive(po)}
                          >
                            Received
                          </button>
                        )}
                        {po.status === "received" && (
                          <span style={{ fontSize: "12px", color: "#1a7f37" }}>
                            Received{(canViewApAging || canVerifyBills) && (po.vendorBill?.verifiedAt ? " · billed" : ", awaiting vendor bill")}.
                          </span>
                        )}
                        {po.status === "bill_verified" &&
                          (canViewApAging || canVerifyBills) && (
                            <span style={{ fontSize: "12px", color: "#1a5fb4" }}>
                              Bill verified.
                            </span>
                          )}
                        {canManage && (
                          <button
                            style={{ ...styles.button, color: "red" }}
                            onClick={() => remove(po)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </>
                  )}

                  {/* Audit trail is for managers/admins/PO-holders, not techs */}
                  {(canCreatePOs || canManage) &&
                    (po.auditTrail || []).length > 0 && (
                      <div style={{ marginTop: "6px" }}>
                        <button
                          style={{ ...styles.button, padding: "2px 8px", fontSize: "12px" }}
                          onClick={() =>
                            setShowAuditId(showAuditId === po.id ? null : po.id)
                          }
                        >
                          {showAuditId === po.id
                            ? "Hide audit trail"
                            : "Audit trail"}{" "}
                          ({po.auditTrail.length})
                        </button>
                        {showAuditId === po.id && (
                          <AuditTrail trail={[...po.auditTrail].reverse()} />
                        )}
                      </div>
                    )}
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Vendor-bill payment modal (Phase B AP). */}
      <PaymentEntryModal
        open={!!vendorPaymentTarget}
        title="Record vendor payment"
        subtitle={
          vendorPaymentTarget
            ? `${vendorPaymentTarget.poNumber || "(no PO #)"} · ${
                vendorPaymentTarget.vendorName ||
                vendorPaymentTarget.vendor ||
                "vendor"
              } · Invoice ${
                vendorPaymentTarget.vendorBill?.invoiceNumber || "—"
              }`
            : ""
        }
        billTotal={vendorPaymentTarget?.vendorBill?.totalAmount || 0}
        alreadyPaid={vendorPaymentTarget?.vendorBill?.paidAmount || 0}
        onCancel={() => setVendorPaymentTarget(null)}
        onConfirm={async (payment) => {
          await actions.recordVendorBillPayment(
            vendorPaymentTarget.id,
            payment
          );
          setVendorPaymentTarget(null);
        }}
      />
    </>
  );
}
